// Session writes only. Tokens stay in memory and in request bodies (no new CORS
// header); never persist them in storage or automatically replay a mutation.
export function isForumMutation(url, method = 'get', base = window.location.origin) {
  if (!['post', 'put', 'patch', 'delete'].includes(String(method).toLowerCase())) return false;
  let target;
  try { target = new URL(url, new URL(base, window.location.origin)); } catch (_) { return false; }
  const allowedOrigins = [window.location.origin];
  try { allowedOrigins.push(new URL(process.env.VUE_APP_API || '/', window.location.origin).origin); } catch (_) { /* same-origin only */ }
  if (!allowedOrigins.includes(target.origin)) return false;
  const path = target.pathname;
  return (path.startsWith('/forum/api/') && path !== '/forum/api/search')
    || /^\/api\/v1\/portfolio\/waitings\/add\/[^/]+\/?$/.test(path)
    || /^\/forum\/(?:delete-post|delete-comment|delete-content|edit-content)\//.test(path)
    || /^\/api\/v1\/forum\/comments\/(?:add|vote)(?:\/|$)/.test(path)
    || /^\/eurekers\/vdb\/(?:clear-forum-notifications|clear-single-forum-notification|silence-single-forum-notification|silence-all-forum-notifications|clear-read-forum-notifications)$/.test(path);
}

const sessionError = () => Object.assign(new Error('La sesión ha cambiado. Recarga la página antes de publicar.'), { code: 'forum_session_changed' });

export function withForumToken(config, token) {
  const headers = { ...(config.headers || {}) };
  let data;
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    data = new FormData();
    config.data.forEach((value, key) => data.append(key, value));
    data.set('csrf_token', token);
    Object.keys(headers).forEach(key => { if (key.toLowerCase() === 'content-type') delete headers[key]; });
  } else if (config.data instanceof URLSearchParams) {
    data = new URLSearchParams(config.data.toString());
    data.set('csrf_token', token);
    headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
  } else {
    const source = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
    if (source !== undefined && source !== null && (typeof source !== 'object' || Array.isArray(source))) throw new Error('Formato de envío no válido.');
    data = { ...(source || {}), csrf_token: token };
    headers['Content-Type'] = 'application/json';
  }
  return { ...config, data, headers };
}

export function createForumCsrfProtection({ fetchSession, getIdentity, now = Date.now }) {
  let cached = null, pending = null, generation = 0;
  const identity = () => JSON.stringify(getIdentity());
  const invalidate = () => { generation += 1; cached = null; pending = null; };
  const token = async () => {
    const key = identity();
    if (cached && cached.key === key && cached.expires > now()) return cached.value;
    if (pending && pending.key === key) return pending.promise;
    const epoch = generation;
    const promise = Promise.resolve().then(fetchSession).then(session => {
      const current = getIdentity();
      if (epoch !== generation || key !== identity() || !session || !session.uid || String(session.uid) === '0'
          || (current.uid && String(current.uid) !== String(session.uid))) throw sessionError();
      if (typeof session.csrf_token !== 'string' || !session.csrf_token) throw new Error('No se pudo preparar un envío seguro.');
      cached = { key, value: session.csrf_token, expires: now() + 5 * 60 * 1000 };
      return session.csrf_token;
    }).finally(() => { if (pending && pending.promise === promise) pending = null; });
    pending = { key, promise };
    return promise;
  };
  return {
    invalidate,
    async prepare(config) {
      if (!isForumMutation(config.url, config.method, config.baseURL || window.location.origin)) return config;
      if (config.cancelToken) config.cancelToken.throwIfRequested();
      const key = identity();
      // Cancelling one caller must not cancel a token read shared by others.
      const value = await (config.cancelToken ? Promise.race([
        token(), config.cancelToken.promise.then(reason => { throw reason; })
      ]) : token());
      if (key !== identity()) throw sessionError();
      return withForumToken(config, value);
    }
  };
}
